// Used in login-routing.module.ts

// Angular
import { Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { Router } from "@angular/router";

// RXJS/Store
import * as RouterStore from "#core/router-store";
import { Store, select } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as LoginStore from "../../store";

// Services/Types/Helpers
import { FormHelper } from "#core/helpers";
import { LocalStorage } from "#core/services";

// Validators
import { ProfileValidator, PwdLength } from "#validators/profile.validator";
import { ResetPwdRequest } from "@carnegie-learning/data-access-angular-passport";

@Component({
    selector: "login-reset-pwd",
    templateUrl: "./login-reset-pwd.component.html",
    styleUrls: ["./login-reset-pwd.component.scss"],
    standalone: false
})
export class LoginResetPwdComponent implements OnInit, OnDestroy {
    passwordForm = this.fb.group(
        {
            newPassword: [
                "",
                {
                    validators: [Validators.required, PwdLength()],
                },
            ],
            confirmPassword: ["", { validators: [Validators.required] }],
        },
        { validator: ProfileValidator.matchPassword }
    );
    hide1 = true; // Password visibility for new Pwd
    hide2 = true; // Password visibility for confirm Pwd
    maintenanceMode: boolean = (<any>window).maintenanceMode;
    bannerStatus$?: Observable<boolean>;
    bannerMessage$?: Observable<string>;
    bannerColor$?: Observable<string>;
    showProgressBar = false;
    formSubmitted = false;
    hash = "";
    private ngUnsubscribe = new Subject();

    constructor(
        private router: Router,
        public loginStore: Store<LoginStore.UserState>,
        public routerStore: Store<RouterStore.State>,
        private localStorage: LocalStorage,
        private fb: UntypedFormBuilder,
        public formHelper: FormHelper
    ) {}

    ngOnInit() {
        this.loginStore.dispatch(LoginStore.userLoginResetState());

        this.bannerStatus$ = this.loginStore.pipe(
            select(LoginStore.getLoginBannerStatus)
        );
        this.bannerMessage$ = this.loginStore.pipe(
            select(LoginStore.getLoginBannerMessage)
        );
        this.bannerColor$ = this.loginStore.pipe(
            select(LoginStore.getLoginBannerColor)
        );

        this.loginStore
            .pipe(select(LoginStore.getLoginBannerColor))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data) => {
                if (data !== "primary") {
                    this.showProgressBar = false;
                }
            });

        if (this.maintenanceMode) {
            this.loginStore.dispatch(LoginStore.userLoginMaintenance());
            this.showProgressBar = false;
        } else {
            this.routerStore
                .pipe(select(RouterStore.getRouterState))
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((data) => {
                    if (
                        data &&
                        data.state.url.includes("reset-password/form")
                    ) {
                        this.hash = data.state.queryParams["reset-hash"];

                        // Clear out local storage and reset state
                        this.localStorage.clear();
                        this.loginStore.dispatch(
                            LoginStore.userProfileResetState()
                        );

                        if (this.hash === undefined || this.hash === null) {
                            this.loginStore.dispatch(
                                LoginStore.userResetPwdNoHash()
                            );
                        }
                    }
                });
        }
    }

    validate(field: string, type: string) {
        return this.formHelper.Validate(this.passwordForm, field, type);
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }

    markAsTouched(event: KeyboardEvent) {
        this.passwordForm.markAsTouched();
    }

    // password validation chip color
    chipColor(type: string) {
        if (!this.passwordForm.get("newPassword")?.touched) {
            // initial state
            return "info";
        }

        const result = this.formHelper.Validate(
            this.passwordForm,
            "newPassword",
            type
        );
        return result ? "info" : "primary";
    }

    submit() {
        if (this.passwordForm.valid) {
            // prevents the enter button from triggering multiple request
            this.passwordForm.markAsUntouched();

            this.showProgressBar = true;
            this.formSubmitted = true;
            if (this.hash) {
                const payload: ResetPwdRequest = {
                    resetHash: this.hash,
                    password: this.passwordForm.get("newPassword")?.value,
                };
                this.loginStore.dispatch(
                    LoginStore.userResetPwdAttempt({ payload: payload })
                );
            }
        }
    }
    cancel() {
        this.router.navigate(["login/form"]);
    }
}
